import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Trip, SubRouteModal } from 'app/modules/components/modals/dto/routes-detail-by-businesss-partnert-vehicle-dto';
import { RouteInfo } from 'app/modules/components/routing/sub-routes/client-route-locator/interfaces/client-route-locator.interface';
import { FormatOtherDateHourPipe } from 'app/pipes/other-date.pipe';
import { TripStatusBadgeComponent } from 'app/shared/components/trip-status-badge/trip-status-badge.component';
import { SubRouteNavigationComponent } from 'app/shared/components/sub-route-navigation/sub-route-navigation.component';
import { RouteNavigationComponent } from 'app/shared/components/route-navigation/route-navigation.component';
import { BusinessPartnerNavigationComponent } from 'app/shared/components/business-partner-navigation/business-partner-navigation.component';
import { TripNavigationComponent } from 'app/shared/components/trip-navigation/trip-navigation.component';

export interface VehicleTripsModalData {
  trips: Trip[];
  vehicleName: string;
}

@Component({
  selector: 'app-vehicle-trips-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    FormatOtherDateHourPipe,
    TripStatusBadgeComponent,
    SubRouteNavigationComponent,
    RouteNavigationComponent,
    BusinessPartnerNavigationComponent,
    TripNavigationComponent
  ],
  templateUrl: './vehicle-trips-modal.component.html',
  styleUrl: './vehicle-trips-modal.component.scss'
})
export class VehicleTripsModalComponent {
  protected readonly data = inject<VehicleTripsModalData>(MAT_DIALOG_DATA);

  /** Max territory chips shown inline before collapsing into a "+N" chip. */
  private readonly MAX_VISIBLE_TERRITORIES = 2;

  readonly displayedColumns = ['id', 'status', 'subRoute', 'territory', 'function', 'date', 'user'];
  dataSource: SubRouteModal[] = [];

  constructor() {
    this.processTrips();
  }

  private processTrips(): void {
    const trips = this.data.trips ?? [];

    this.dataSource = trips.map((trip: Trip) => {
      const routes: RouteInfo[] = (trip.routes ?? []).map(route => ({
        _id: route._id,
        id: route.id,
        name: route.name,
        statusId: route.statusId,
        externalKeys: route.externalKeys
      }));

      return {
        _id: trip._id,
        id: trip.id,
        statusId: trip.statusId,
        statusTripId: trip.statusTripId,
        nameSubRoute: trip.subRoute?.name ?? '',
        function: trip.subRoute?.function?.name ?? '',
        assignedUser: trip.user?.business_partner_id
          ? trip.user.business_partner_name
          : trip.user?.user_login?.username ?? '',
        subRouteCode: trip.subRoute?.externalKeys?.code ?? '',
        functionCode: trip.subRoute?.function?.code ?? '',
        dateHourEndTeoric: trip.dateHourEndTeoric ?? '',
        dateHourStartTeoric: trip.dateHourStartTeoric ?? '',
        assignedUserCode: trip.user?.business_partner_id
          ? trip.user.business_partner_external_keys?.code ?? ''
          : '',
        routes
      };
    });
  }

  getTerritoriesTrips(element: SubRouteModal): RouteInfo[] {
    return element.routes ?? [];
  }

  /** First N territories shown as chips; the rest collapse into a "+N" chip. */
  visibleTerritories(element: SubRouteModal): RouteInfo[] {
    return this.getTerritoriesTrips(element).slice(0, this.MAX_VISIBLE_TERRITORIES);
  }

  hiddenTerritoriesCount(element: SubRouteModal): number {
    return Math.max(0, this.getTerritoriesTrips(element).length - this.MAX_VISIBLE_TERRITORIES);
  }

  hasTrip(element: SubRouteModal): boolean {
    return !!element?.id;
  }
}
